import { useCallback, useRef, useState } from 'react';

import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import { Paginated, Pagination, PaginationLink } from '~services/pagination';
import { getPageNumber } from '~utils/pagination';

interface CursorPageRequest {
  cursor?: string;
  link?: PaginationLink;
  page: number;
}

interface FetchCursorPageParams {
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  signal: AbortSignal;
}

interface UseCursorPaginationOptions<Row> {
  fetchPage: (params: FetchCursorPageParams) => Promise<Paginated<Row>>;
  limit: number;
  onFetchError?: (error: unknown) => void;
}

const FIRST_PAGE_REQUEST: CursorPageRequest = { page: 1 };

// useDeletedRecordsCursor keeps only the cursor and leaves fetching to React Query; this
// hook also owns the rows, because its grids edit them in place between page loads
export default function useCursorPagination<Row>({
  fetchPage,
  limit,
  onFetchError,
}: UseCursorPaginationOptions<Row>) {
  const [rows, setRows] = useState<Row[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ limit, page: 1 });
  const [isFetching, setIsFetching] = useState(false);
  const currentPageRequestRef = useRef(FIRST_PAGE_REQUEST);
  const issueRequestSignal = useLatestRequestSignal();
  // the loaders below are stable, so they read the caller's latest fetcher from here
  const callbacksRef = useRef({ fetchPage, onFetchError });
  callbacksRef.current = { fetchPage, onFetchError };

  const loadPage = useCallback(
    async (request: CursorPageRequest): Promise<void> => {
      // a newer request aborts this one: its response belongs to a list that is no longer
      // on screen, and recording it would mix another record's cursors into this one
      const signal = issueRequestSignal();
      setIsFetching(true);

      try {
        const result = await callbacksRef.current.fetchPage({
          cursor: request.cursor,
          limit,
          link: request.link,
          signal,
        });
        if (signal.aborted) return;

        // deleting the only row on a later page leaves its cursor pointing at nothing
        const pageEmptiedByDelete = result.data.length === 0 && Boolean(request.link);
        if (pageEmptiedByDelete) {
          return loadPage(FIRST_PAGE_REQUEST);
        }

        currentPageRequestRef.current = request;
        setRows(result.data);
        setPagination({ ...result.pagination, limit, page: request.page });
      } catch (error) {
        if (signal.aborted) return;

        callbacksRef.current.onFetchError?.(error);
      } finally {
        // a superseded request settling must not clear the newer request's spinner
        if (!signal.aborted) {
          setIsFetching(false);
        }
      }
    },
    [issueRequestSignal, limit],
  );

  const goToPage = useCallback(
    (link?: PaginationLink) => {
      const cursor = link ? pagination[link] : undefined;

      if (!link || !cursor) {
        return loadPage(FIRST_PAGE_REQUEST);
      }

      return loadPage({ cursor, link, page: getPageNumber(pagination.page, link) });
    },
    [loadPage, pagination],
  );

  const reloadCurrentPage = useCallback(
    () => loadPage(currentPageRequestRef.current),
    [loadPage],
  );

  // drops the previous record's rows and cursors before its first page loads, so a save
  // or delete that lands in between can't reload the new record with them
  const startOver = useCallback(() => {
    currentPageRequestRef.current = FIRST_PAGE_REQUEST;
    setRows([]);
    setPagination({ limit, page: 1 });

    return loadPage(FIRST_PAGE_REQUEST);
  }, [limit, loadPage]);

  return {
    goToPage,
    isFetching,
    pagination,
    reloadCurrentPage,
    rows,
    setRows,
    startOver,
  };
}
